import React, { useState, useEffect, useRef } from 'react';
import { TextField, Box, Typography, List, ListItem, Paper, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { debounce } from 'lodash';
import wineRegionService from '../services/wineRegionService';

// Interface for wine region suggestion
export interface WineRegionSuggestion {
  id: string;
  name: string;
  path?: string;
  hierarchyNames?: string[];
}

interface RegionSearchBoxProps {
  onSearch: (query: string) => void;
  onSelectRegion: (regionId: string) => void;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
}

/**
 * Region search box component with autocomplete
 */
const RegionSearchBox: React.FC<RegionSearchBoxProps> = ({
  onSearch,
  onSelectRegion,
  fullWidth = false,
  size = 'small'
}) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<WineRegionSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Debounce the search to avoid too many API calls
  const fetchSuggestions = useRef(
    debounce(async (query: string) => {
      console.log('Fetching suggestions for query:', query);
      if (query.trim().length === 0) {
        console.log('Empty query, clearing suggestions');
        setSuggestions([]);
        setLoading(false);
        return;
      }

      try {
        console.log('Calling suggestions API...');
        const response = await wineRegionService.getWineRegionSuggestions(query);
        console.log('Suggestions API response:', response);
        
        if (response && response.success && response.data && response.data.suggestions && Array.isArray(response.data.suggestions)) {
          console.log(`Found ${response.data.suggestions.length} suggestions`);
          
          // Log each suggestion for debugging
          response.data.suggestions.forEach((item: WineRegionSuggestion, index: number) => {
            console.log(`Suggestion ${index}:`, item);
          });
          
          setSuggestions(response.data.suggestions);
          setShowSuggestions(response.data.suggestions.length > 0);
        } else {
          console.warn('Invalid suggestions format or no suggestions found:', response);
          setSuggestions([]);
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300)
  ).current;

  // Effect to handle input changes
  useEffect(() => {
    if (inputValue.trim().length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading(true);
    fetchSuggestions(inputValue);

    return () => {
      fetchSuggestions.cancel();
    };
  }, [inputValue, fetchSuggestions]);

  // Handle clicks outside the search box to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion: WineRegionSuggestion) => {
    console.log('Selected suggestion:', suggestion);
    if (suggestion && suggestion.id) {
      onSelectRegion(suggestion.id);
      setInputValue('');
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle search submission
  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (inputValue.trim()) {
      onSearch(inputValue);
      setShowSuggestions(false);
    }
  };

  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);
    if (value.trim().length > 0) {
      setShowSuggestions(true);
    }
  };

  // Clear input
  const handleClearInput = () => {
    setInputValue('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <Box ref={searchRef} sx={{ position: 'relative', width: fullWidth ? '100%' : '300px' }}>
      <form onSubmit={handleSearchSubmit}>
        <TextField
          fullWidth
          size={size}
          label="Search regions"
          variant="outlined"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => inputValue.trim().length > 0 && setShowSuggestions(true)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {loading ? (
                  <CircularProgress size={20} />
                ) : inputValue ? (
                  <IconButton size="small" onClick={handleClearInput}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : null}
              </InputAdornment>
            ),
          }}
        />
      </form>

      {/* Suggestions dropdown */}
      {showSuggestions && (
        <Paper 
          elevation={3} 
          sx={{
            position: 'absolute',
            width: '100%',
            zIndex: 1000,
            mt: 0.5,
            maxHeight: '300px',
            overflow: 'auto'
          }}
        >
          <List sx={{ p: 0 }}>
            {suggestions.length > 0 ? (
              suggestions.map((suggestion) => (
                <ListItem 
                  key={suggestion.id} 
                  onClick={() => handleSelectSuggestion(suggestion)}
                  sx={{ 
                    display: 'block', 
                    py: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' } 
                  }}
                >
                  <Typography variant="body1" fontWeight="medium">
                    {suggestion.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {suggestion.path || (suggestion.hierarchyNames && suggestion.hierarchyNames.join(' > '))}
                  </Typography>
                </ListItem>
              ))
            ) : (
              <ListItem sx={{ py: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  No regions found
                </Typography>
              </ListItem>
            )}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default RegionSearchBox;
